import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { View } from "native-base";
import { InteractionManager, ScrollView } from "react-native";
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { useAppContext } from "../Universal/API/Context-API/AppContext";
import { FullScreenSpinner } from "../Universal/SimpleCenteredSpinner/SimpleCenteredSpinner";
import { BackButtonBackHome } from "../Universal/Buttons/BackButtonBackHomeModified";
import ExpandedImageSlider from "../Universal/ExpandedImage/ExpandedSliderImage";
import { useSmartNavigate } from "../Universal/API/Context-API/hooks/useSmartNavigate";
import { formPrice } from "../HomeScreen/Components/Unit";
import { useUserState } from "../Universal/API/Context-API/hooks/useUserState";
import { useCart } from "../Universal/API/Context-API/CartContext";
import { formUnit } from "../HomeScreen/HomeScreen";
import { copyArt } from "../HomeScreen/Components/utils";
import { handleImageSwiper } from "./ImageSwiper/ImageSwiper";
import { getUnitFullLayoutMetrics } from "./utils";
import UnitFullV2Gallery from "./Components/UnitFullV2/Gallery/Gallery";
import UnitFullV2Header from "./Components/UnitFullV2/Header";
import UnitFullV2Summary from "./Components/UnitFullV2/Summary";
import UnitFullV2ProductDescription from "./Components/UnitFullV2/ProductDescription";
import UnitFullV2TechnicalInfo from "./Components/UnitFullV2/TechnicalInfo";
import { buildQuickFacts, hasStock } from "./Components/UnitFullV2/helpers";
import { useResolve } from "./Components/UnitFullV2/hooks/useResolve";
import { createUnitFullV2Styles } from "./Components/UnitFullV2/styles";
import {
  ColorOption,
  SizeOption,
  UnitFullV2Props,
  VariantGroupMatchEntry,
  VariantGroupPayload,
} from "./Components/UnitFullV2/types";

const UnitFullV2: React.FC<UnitFullV2Props> = ({ navigation, route }) => {
  const { idUnit, fromRoute, collection_name } = route.params;
  const {
    allUnitData,
    variantGroupsData,
    scaleAll,
    isSmallVersion,
    currentWidth,
    isMediumVersion,
  } = useAppContext();
  const { buildColorOptions, buildSizeOptions, buildTechnicalSpecs, resolveSwatchColor } =
    useResolve();
  const { floatingCartBottomInset } = useCart();
  const userState = useUserState();
  const { smartNavigate } = useSmartNavigate({ navigation });
  const [activeUnitId, setActiveUnitId] = useState<string | number>(idUnit);
  const effectiveUnitId = activeUnitId ?? idUnit;
  const [allowHeavyRender, setAllowHeavyRender] = useState(false);
  const [showExpanded, setShowExpanded] = useState(false);
  const [expandedStartIndex, setExpandedStartIndex] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTechOpen, setIsTechOpen] = useState(true);
  const [techContentHeight, setTechContentHeight] = useState(0);

  const imageSwiperRef = useRef<handleImageSwiper>(null);
  const techHeightProgress = useSharedValue(1);
  const techChevronProgress = useSharedValue(1);
  const contentSwapProgress = useSharedValue(1);

  useEffect(() => {
    setActiveUnitId(idUnit);
  }, [idUnit]);

  useEffect(() => {
    let cancelled = false;

    const fallbackId = setTimeout(() => {
      if (!cancelled) setAllowHeavyRender(true);
    }, 250);

    const task = InteractionManager.runAfterInteractions(() => {
      if (cancelled) return;
      clearTimeout(fallbackId);
      setAllowHeavyRender(true);
    });

    return () => {
      cancelled = true;
      clearTimeout(fallbackId);
      task?.cancel?.();
    };
  }, []);

  useEffect(() => {
    techHeightProgress.value = withTiming(isTechOpen ? 1 : 0, {
      duration: 280,
      easing: Easing.out(Easing.cubic),
    });
    techChevronProgress.value = withTiming(isTechOpen ? 1 : 0, {
      duration: 240,
      easing: Easing.out(Easing.cubic),
    });
  }, [isTechOpen, techChevronProgress, techHeightProgress]);

  useEffect(() => {
    if (!allowHeavyRender) return;

    contentSwapProgress.value = 0;
    contentSwapProgress.value = withTiming(1, {
      duration: 220,
      easing: Easing.out(Easing.cubic),
    });
  }, [allowHeavyRender, contentSwapProgress, effectiveUnitId]);

  const currentUnit = useMemo(
    () => (allUnitData ? allUnitData[effectiveUnitId] ?? null : null),
    [allUnitData, effectiveUnitId]
  );

  const variantGroup = useMemo(() => {
    if (!variantGroupsData || !currentUnit?.id) return null;
    return (
      variantGroupsData[String(currentUnit.id)] ??
      variantGroupsData[Number(currentUnit.id)] ??
      null
    ) as VariantGroupPayload | null;
  }, [currentUnit?.id, variantGroupsData]);

  const buildOptionsFromVariantGroup = useCallback(
    (
      matchMap: Record<string, VariantGroupMatchEntry> | undefined,
      type: "color" | "size"
    ) => {
      if (!currentUnit || !matchMap) return [];

      const currentId = String(currentUnit.id);
      const entries = Object.entries(matchMap)
        .map(([key, entry]) => {
          const ids = Array.isArray(entry?.ids)
            ? entry.ids.map((value) => String(value)).filter(Boolean)
            : [];

          if (ids.length === 0) return null;

          const activeId = ids.find((value) => value === currentId);
          const nextId = activeId ?? ids[0];
          const label = String(entry?.label ?? key).trim() || key;

          if (type === "color") {
            return {
              key: `${type}_${key}`,
              label,
              swatch: resolveSwatchColor(label, currentUnit.poverh),
              unitId: nextId,
              isActive: Boolean(activeId),
            };
          }

          return {
            key: `${type}_${key}`,
            label,
            unitId: nextId,
            isActive: Boolean(activeId),
          };
        })
        .filter(Boolean);

      return entries;
    },
    [currentUnit, resolveSwatchColor]
  );

  const styles = useMemo(() => createUnitFullV2Styles(scaleAll), [scaleAll]);

  const uniqueImages = useCallback((images: string[]) => {
    const seen = new Set<string>();

    return images.filter((image) => {
      const normalized = String(image || "").trim();
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
  }, []);

  const layout = useMemo(
    () =>
      getUnitFullLayoutMetrics({
        currentWidth,
        isSmallVersion,
        isMediumVersion,
        scaleAll,
      }),
    [currentWidth, isMediumVersion, isSmallVersion, scaleAll]
  );

  const goToCollection = useCallback(
    (nextCollectionName: string) => {
      smartNavigate({
        navigation,
        goTo: "Collection",
        data: { collection_name: nextCollectionName },
      });
    },
    [navigation, smartNavigate]
  );

  const handleCopyArt = useCallback((art: string) => {
    copyArt({ art });
  }, []);

  const handleBack = useCallback(() => {
    if (fromRoute === "Collection" && collection_name) {
      smartNavigate({
        navigation,
        goTo: "Collection",
        data: { collection_name },
        replace: true,
      });
      return;
    }

    if (navigation?.canGoBack?.()) {
      navigation.goBack();
      return;
    }

    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
      return;
    }

    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  }, [collection_name, fromRoute, navigation, smartNavigate]);

  const priceText = useMemo(() => {
    if (!currentUnit) return "";
    return formPrice({
      unit: formUnit(currentUnit),
      isDiscount: userState.isUserLoogedInAndShowDiscountPrices,
    });
  }, [currentUnit, userState.isUserLoogedInAndShowDiscountPrices]);

  const available = useMemo(() => (currentUnit ? hasStock(currentUnit) : false), [currentUnit]);

  const colorOptions = useMemo(() => {
    if (variantGroup) {
      return buildOptionsFromVariantGroup(variantGroup.color_match, "color") as ColorOption[];
    }
    return currentUnit ? buildColorOptions(currentUnit, allUnitData) : [];
  }, [allUnitData, buildColorOptions, buildOptionsFromVariantGroup, currentUnit, variantGroup]);

  const sizeOptions = useMemo(() => {
    if (variantGroup) {
      return buildOptionsFromVariantGroup(variantGroup.size_match, "size") as SizeOption[];
    }
    return currentUnit ? buildSizeOptions(currentUnit, allUnitData) : [];
  }, [allUnitData, buildOptionsFromVariantGroup, buildSizeOptions, currentUnit, variantGroup]);

  const handleSelectColorUnit = useCallback(
    (nextUnitId: string | number) => {
      if (String(nextUnitId) === String(currentUnit?.id)) return;

      const nextParams = {
        idUnit: nextUnitId,
        ...(fromRoute ? { fromRoute } : {}),
        ...(collection_name ? { collection_name } : {}),
      };

      setActiveUnitId(nextUnitId);
      setCurrentIndex(0);

      if (typeof window !== "undefined") {
        const query = window.location.search || "";
        window.history.replaceState(null, "", `/${nextUnitId}${query}`);
        return;
      }

      smartNavigate({
        navigation,
        goTo: "Unit",
        data: nextParams,
        replace: true,
      });
    },
    [collection_name, currentUnit?.id, fromRoute, navigation, smartNavigate]
  );

  const technicalSpecs = useMemo(() => {
    if (!currentUnit) return [];
    return buildTechnicalSpecs({
      unit: currentUnit,
      isAvailable: available,
      scaleAll,
    });
  }, [available, buildTechnicalSpecs, currentUnit, scaleAll]);

  const galleryImages = useMemo(() => {
    if (!currentUnit) return [];
    if (currentUnit.sliders.full?.length) {
      return uniqueImages(currentUnit.sliders.full);
    }
    return currentUnit.uri_full ? uniqueImages([currentUnit.uri_full]) : [];
  }, [currentUnit, uniqueImages]);

  const thumbImages = useMemo(() => {
    if (!currentUnit) return [];
    if (currentUnit.sliders.medium?.length) {
      return uniqueImages(currentUnit.sliders.medium);
    }
    return galleryImages;
  }, [currentUnit, galleryImages, uniqueImages]);

  const openExpanded = useCallback((index: number) => {
    if (!galleryImages.length) return;
    setExpandedStartIndex(index);
    setShowExpanded(true);
  }, [galleryImages.length]);

  const quickFacts = useMemo(
    () => (currentUnit ? buildQuickFacts(currentUnit) : []),
    [currentUnit]
  );

  const heroWidth = useMemo(
    () =>
      layout.canUseWideLayout
        ? layout.mediaColumnWidth ?? layout.imageWidth
        : layout.imageWidth,
    [layout.canUseWideLayout, layout.imageWidth, layout.mediaColumnWidth]
  );

  const techBodyAnimatedStyle = useAnimatedStyle(() => ({
    height: techContentHeight * techHeightProgress.value,
    opacity: interpolate(techHeightProgress.value, [0, 1], [0.4, 1]),
    overflow: "hidden",
  }));

  const techChevronAnimatedStyle = useAnimatedStyle(() => ({
    transform: [
      {
        rotate: `${interpolate(techChevronProgress.value, [0, 1], [0, 180])}deg`,
      },
    ],
  }));

  const contentSwapAnimatedStyle = useAnimatedStyle(() => ({
    opacity: interpolate(contentSwapProgress.value, [0, 1], [0.72, 1]),
    transform: [
      {
        translateY: interpolate(contentSwapProgress.value, [0, 1], [10, 0]),
      },
    ],
  }));

  if (!currentUnit) {
    return (
      <View style={styles.main}>
        <BackButtonBackHome navigation={navigation} />
        <FullScreenSpinner />
      </View>
    );
  }

  return (
    <View style={styles.main}>
      {showExpanded && (
        <ExpandedImageSlider
          visible={showExpanded}
          images={galleryImages}
          initialIndex={expandedStartIndex}
          onClose={() => setShowExpanded(false)}
          title={currentUnit.name}
          enableBackdropClose={true}
        />
      )}

      <BackButtonBackHome navigation={navigation} onPress={handleBack} />

      {!allowHeavyRender ? (
        <FullScreenSpinner />
      ) : (
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: floatingCartBottomInset },
          ]}
          showsVerticalScrollIndicator={false}
        >
          <Animated.View
            style={[
              styles.shell,
              contentSwapAnimatedStyle,
              layout.canUseWideLayout && { width: layout.desktopContentWidth },
            ]}
          >
            <UnitFullV2Header
              currentUnit={currentUnit}
              isSmallVersion={isSmallVersion}
              quickFacts={quickFacts}
              scaleAll={scaleAll}
              styles={styles}
              onCopyArt={handleCopyArt}
              onGoToCollection={goToCollection}
            />

            <View
              style={[
                styles.heroGrid,
                layout.canUseWideLayout ? styles.heroGridWide : styles.heroGridStack,
              ]}
            >
              <View
                style={[
                  styles.galleryColumn,
                  layout.canUseWideLayout && {
                    width: layout.mediaColumnWidth ?? heroWidth,
                    marginRight: 24 * scaleAll,
                  },
                ]}
              >
                <UnitFullV2Gallery
                  currentIndex={currentIndex}
                  currentUnit={currentUnit}
                  galleryImages={galleryImages}
                  heroWidth={heroWidth}
                  imageSwiperRef={imageSwiperRef}
                  isSmallVersion={isSmallVersion}
                  layout={layout}
                  scaleAll={scaleAll}
                  styles={styles}
                  thumbImages={thumbImages}
                  onIndexChange={setCurrentIndex}
                  onOpenExpanded={openExpanded}
                />
              </View>

              <View
                style={[
                  styles.infoColumn,
                  layout.canUseWideLayout && {
                    width: layout.detailsColumnWidth ?? 460,
                    marginTop: 0,
                  },
                ]}
              >
                <UnitFullV2Summary
                  available={available}
                  colorOptions={colorOptions}
                  sizeOptions={sizeOptions}
                  currentUnit={currentUnit}
                  isSmallVersion={isSmallVersion}
                  priceText={priceText}
                  scaleAll={scaleAll}
                  styles={styles}
                  onCopyArt={handleCopyArt}
                  onGoToCollection={goToCollection}
                  onSelectColorUnit={handleSelectColorUnit}
                  onSelectSizeUnit={handleSelectColorUnit}
                />
              </View>
            </View>

            <UnitFullV2ProductDescription
              country={currentUnit.country}
              description={currentUnit.descriptionUser}
              imageUri={galleryImages[0] ?? currentUnit.uri_full}
              isSmallVersion={isSmallVersion}
              scaleAll={scaleAll}
              styles={styles}
            />

            <UnitFullV2TechnicalInfo
              isSmallVersion={isSmallVersion}
              scaleAll={scaleAll}
              setTechContentHeight={setTechContentHeight}
              styles={styles}
              technicalSpecs={technicalSpecs}
              techBodyAnimatedStyle={techBodyAnimatedStyle}
              techChevronAnimatedStyle={techChevronAnimatedStyle}
              techContentHeight={techContentHeight}
              onGoToCollection={goToCollection}
              onToggle={() => setIsTechOpen((prev) => !prev)}
            />
          </Animated.View>
        </ScrollView>
      )}
    </View>
  );
};

export default React.memo(UnitFullV2);
